/**
 * MessageRow — chat-row anatomy.
 *
 * A single message in a chat scroller. Five roles, each with its own visual
 * treatment but a shared slot vocabulary (`avatar`, `actions`, `timestamp`,
 * children-as-content):
 *
 *   - `user`      — right-aligned bubble, soft primary tint, actions on hover left of bubble.
 *   - `assistant` — left-aligned bare text, AIKO mark on the leading edge, actions below.
 *   - `tool`      — left-aligned card-style host (ToolCard goes inside as content).
 *   - `system`    — full-width centered divider for thread-state cues ("Returned to AIKO chat").
 *   - `error`     — left-aligned destructive surface for stream-interruption / tool-failure cues.
 *
 * Slots are passed as props rather than compound children so the per-role
 * layout owns where each slot renders. Pass `null` / omit to opt out.
 *
 * Default entrance animation is a 240ms fade+slide-up (`prefers-reduced-motion`
 * forces it off; `animate={false}` opts out per call). Action groups are
 * CSS-only (`group-hover`) so they don't compete with the entrance animation
 * timing, and `actionsVisibility="always"` opts a surface out of hover-gating
 * entirely (touch has no hover; see ActionsSlot).
 *
 * For the empty placeholder while AIKO composes, render a `<TypingIndicator>`
 * inside `<MessageRow role="assistant">` rather than reaching for a custom
 * surface — same rhythm, same bubble, same spacing.
 */
import * as React from "react";
import { motion, type HTMLMotionProps } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";
import { useReducedMotion } from "../hooks/useReducedMotion";

/* ============================================================================
 * Types
 * ========================================================================= */

export type MessageRole = "user" | "assistant" | "tool" | "system" | "error";

const messageRowVariants = cva("flex w-full", {
  variants: {
    role: {
      user: "justify-end",
      assistant: "justify-start",
      tool: "justify-start",
      system: "justify-center my-2",
      error: "justify-start",
    },
  },
  defaultVariants: { role: "assistant" },
});

/**
 * Whether the actions row is revealed on hover (the default, and what every
 * consumer got before this prop existed) or always visible.
 */
export type ActionsVisibility = "hover" | "always";

export interface MessageRowProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "role">,
    VariantProps<typeof messageRowVariants> {
  /** Speaker / message kind. Drives the entire layout. */
  role: MessageRole;
  /** Avatar slot. Conventionally a `<AikoMark>` for assistant, `<Avatar>` for user. */
  avatar?: React.ReactNode;
  /** Action affordances — copy, retry, share. */
  actions?: React.ReactNode;
  /**
   * How `actions` is revealed. `hover` (default) matches the historical
   * behaviour; `always` keeps them visible at rest, which is what a surface
   * needs when the controls must be reachable on touch (there is no hover
   * there) or discoverable without exploration. See ActionsSlot below.
   */
  actionsVisibility?: ActionsVisibility;
  /** Relative or absolute time. Conventionally a `<time>` element. */
  timestamp?: React.ReactNode;
  /** Message body — plain string, `<Markdown>`, `<TypingIndicator>`, `<ToolCard>`, etc. */
  children?: React.ReactNode;
  /**
   * Disable Framer Motion entrance animation. Default true.
   * `prefers-reduced-motion` forces this off regardless.
   */
  animate?: boolean;
  /**
   * Display name for the sender. Rendered as small muted text above the
   * message body for `assistant` and `tool` roles only.
   * No-op for `user`, `system`, and `error` — those roles have their own
   * identification affordances (avatar position, divider label, destructive tint).
   */
  senderName?: string;
  /**
   * The body has not arrived — renders placeholder lines in place of
   * `children`, inside this role's own layout (avatar, side, bubble tint) so
   * the row is already sitting where the message will sit.
   *
   * `actions` are suppressed while this is true. A hover rail over an unloaded
   * turn offers Copy and Regenerate for text that does not exist yet; they
   * would either no-op or act on the previous message.
   */
  loading?: boolean;
  /**
   * Placeholder line count. Defaults to 1 for `user` and 3 otherwise — a user
   * turn is typically one line and an answer typically several, and a skeleton
   * that ignores that reads as the wrong speaker before the text lands.
   */
  loadingLines?: number;
}

/* ============================================================================
 * Component
 * ========================================================================= */

const BODY_WIDTHS = ["w-full", "w-[92%]", "w-[85%]"] as const;

export const MessageRow = React.forwardRef<HTMLDivElement, MessageRowProps>(
  (
    {
      className,
      role,
      avatar,
      actions,
      actionsVisibility = "hover",
      timestamp,
      children,
      animate = true,
      senderName,
      loading = false,
      loadingLines,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = animate && reduced !== true;

    const wrapperClasses = cn(messageRowVariants({ role }), className);
    const ariaRole = role === "system" || role === "error" ? ("status" as const) : undefined;

    const lineCount = Math.max(1, loadingLines ?? (role === "user" ? 1 : 3));
    const body = loading ? (
      <div aria-hidden="true" className="space-y-2 py-1">
        {Array.from({ length: lineCount }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            animation="shimmer"
            // Last line short: a block of equal-length bars reads as a table.
            className={cn(
              "h-3.5",
              i === lineCount - 1 ? "w-[45%]" : BODY_WIDTHS[i % BODY_WIDTHS.length],
            )}
          />
        ))}
      </div>
    ) : (
      children
    );

    const inner = renderRole({
      role,
      avatar,
      // See `loading` — no controls for a message that isn't there.
      actions: loading ? undefined : actions,
      actionsVisibility,
      timestamp,
      children: body,
      senderName,
    });

    if (shouldAnimate) {
      const motionProps: HTMLMotionProps<"div"> = {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.24, ease: [0.16, 1, 0.3, 1] },
      };
      return (
        <motion.div
          ref={ref}
          role={ariaRole}
          aria-busy={loading || undefined}
          className={wrapperClasses}
          {...motionProps}
          {...(rest as HTMLMotionProps<"div">)}
        >
          {inner}
        </motion.div>
      );
    }

    return (
      <div
        ref={ref}
        role={ariaRole}
        aria-busy={loading || undefined}
        className={wrapperClasses}
        {...rest}
      >
        {inner}
      </div>
    );
  },
);
MessageRow.displayName = "MessageRow";

/* ============================================================================
 * Per-role layouts
 *
 * Each role owns its own structural rendering so future visual changes stay
 * scoped. Shared bits (avatar wrapper, hover-action group, timestamp pill)
 * live as small helpers below — extracted only where the duplication earned
 * the helper, not pre-emptively.
 * ========================================================================= */

interface RoleArgs {
  role: MessageRole;
  avatar?: React.ReactNode;
  actions?: React.ReactNode;
  actionsVisibility: ActionsVisibility;
  timestamp?: React.ReactNode;
  children?: React.ReactNode;
  senderName?: string;
}

function renderRole(args: RoleArgs) {
  switch (args.role) {
    case "user":
      return <UserLayout {...args} />;
    case "assistant":
      return <AssistantLayout {...args} />;
    case "tool":
      return <ToolLayout {...args} />;
    case "system":
      return <SystemLayout {...args} />;
    case "error":
      return <ErrorLayout {...args} />;
  }
}

/** Avatar wrapper — keeps the AikoMark / Avatar from shrinking inside flex. */
function AvatarSlot({ children }: { children: React.ReactNode }) {
  return <div className="shrink-0">{children}</div>;
}

/** Inline timestamp — small muted text, tabular numerals so widths don't jiggle. */
function TimestampSlot({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] leading-none text-muted-foreground/80 tabular-nums select-none">
      {children}
    </span>
  );
}

/**
 * Actions row. `hover` (the default) reveals on the row's group-hover, which is
 * the quiet treatment a long transcript wants. `always` keeps the row visible at
 * rest, for surfaces that need the affordances to be findable.
 *
 * Why `always` exists at all: opacity 0 does NOT remove hit-testing, so a
 * hover-only rail on a touch device is a set of controls that are fully tappable
 * and completely invisible. There is no hover to reveal them and `focus-within`
 * cannot help, because focusing them means finding them first. Measured on a
 * consumer surface at phone width: rail opacity 0, buttons 24x24 and hit-testable.
 * Hover is also unavailable to keyboard-only users, and invisible controls are a
 * known barrier for cognitive and motor impairments.
 *
 * `hover` is kept, unchanged and default, because the calm it buys is real on a
 * pointer device with a long thread, and every existing consumer relies on it.
 * The choice belongs to the surface, so it is a prop rather than a new rule.
 *
 * In `always` mode the row does NOT dim, so hover feedback has to come from the
 * controls themselves (a background on the hovered button). That is the caller's
 * styling, deliberately: this slot owns visibility, not button skins.
 */
function ActionsSlot({
  children,
  visibility,
}: {
  children: React.ReactNode;
  visibility: ActionsVisibility;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-1",
        visibility === "hover" &&
          "opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150",
      )}
    >
      {children}
    </div>
  );
}

function UserLayout({ avatar, actions, actionsVisibility, timestamp, children }: RoleArgs) {
  return (
    <div className="group flex items-end gap-2 max-w-[75%] ml-auto">
      {actions ? <ActionsSlot visibility={actionsVisibility}>{actions}</ActionsSlot> : null}
      <div className="flex flex-col items-end gap-1 min-w-0">
        <div className="px-4 py-3 rounded-2xl rounded-br-md text-sm leading-relaxed shadow-sm bg-primary/10 text-foreground break-words [overflow-wrap:anywhere] flex flex-col">
          <div>{children}</div>
          {timestamp ? (
            <span className="self-end mt-1 -mb-1">
              <TimestampSlot>{timestamp}</TimestampSlot>
            </span>
          ) : null}
        </div>
      </div>
      {avatar ? <AvatarSlot>{avatar}</AvatarSlot> : null}
    </div>
  );
}

function AssistantLayout({
  avatar,
  actions,
  actionsVisibility,
  timestamp,
  children,
  senderName,
}: RoleArgs) {
  return (
    <div className="group flex items-start gap-2 w-full">
      {avatar ? (
        <div className="shrink-0 mt-0.5">{avatar}</div>
      ) : (
        // Reserve the leading rail width so consecutive assistant rows align
        // with the avatar above them. Matches v3's "show avatar only on the
        // first of a consecutive run" pattern without forcing consumers to
        // pass a placeholder.
        <div className="shrink-0 w-7" aria-hidden />
      )}
      <div className="flex-1 min-w-0 space-y-1">
        {senderName ? (
          <span className="text-[10px] font-medium text-muted-foreground">{senderName}</span>
        ) : null}
        <div className="text-sm leading-relaxed text-foreground">{children}</div>
        {actions || timestamp ? (
          <div className="flex items-center gap-2 h-6">
            {actions ? <ActionsSlot visibility={actionsVisibility}>{actions}</ActionsSlot> : null}
            {timestamp ? <TimestampSlot>{timestamp}</TimestampSlot> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* `tool` renders its actions RAW, with no visibility slot at all, so they have
   always been permanently visible here. Left as-is: `actionsVisibility` is
   deliberately not threaded in, because there is no hover-gating to opt out of.
   Worth knowing that the library was never uniform on this. */
function ToolLayout({ avatar, actions, timestamp, children, senderName }: RoleArgs) {
  return (
    <div className="flex items-start gap-2 w-full">
      {avatar ? (
        <div className="shrink-0 mt-0.5">{avatar}</div>
      ) : (
        <div className="shrink-0 w-7" aria-hidden />
      )}
      <div className="flex-1 min-w-0 space-y-1">
        {senderName ? (
          <span className="text-[10px] font-medium text-muted-foreground">{senderName}</span>
        ) : null}
        <div>{children}</div>
        {actions || timestamp ? (
          <div className="flex items-center gap-2">
            {timestamp ? <TimestampSlot>{timestamp}</TimestampSlot> : null}
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SystemLayout({ children }: RoleArgs) {
  return (
    <div className="flex items-center gap-3 w-full max-w-md">
      <div className="flex-1 h-px bg-border" aria-hidden />
      <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/80 whitespace-nowrap">
        {children}
      </span>
      <div className="flex-1 h-px bg-border" aria-hidden />
    </div>
  );
}

function ErrorLayout({ avatar, actions, timestamp, children }: RoleArgs) {
  return (
    <div className="flex items-start gap-2 w-full">
      {avatar ? (
        <div className="shrink-0 mt-0.5">{avatar}</div>
      ) : (
        <div className="shrink-0 w-7" aria-hidden />
      )}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {children}
        </div>
        {actions || timestamp ? (
          <div className="flex items-center gap-2">
            {timestamp ? <TimestampSlot>{timestamp}</TimestampSlot> : null}
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
